import { Link, useNavigate } from "react-router-dom";
import { 
  Home, Users, Calendar, MessageCircle, Settings, 
  LogOut, User, ChevronRight, Menu
} from "lucide-react";
import { useState } from "react";
import { Button } from "./ui/button";
import { useAuth } from "../App";

const RAFHA_LOGO = "https://customer-assets.emergentagent.com/job_4c248649-c7ad-49ab-8962-bf67e4ed6b65/artifacts/rogqq55c_F1371478-432E-404F-80CC-D2C1450E0839.png";

const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const menuItems = [
    { icon: Home, label: "Dashboard", href: "/dashboard" },
    { icon: Calendar, label: "Bookings", href: "/dashboard?tab=bookings" },
    { icon: MessageCircle, label: "Messages", href: "/dashboard?tab=messages" },
  ];

  if (user?.role === "vendor") {
    menuItems.push({ icon: Settings, label: "Profile", href: "/dashboard?tab=profile" });
  }

  return (
    <div className="min-h-screen bg-obsidian flex">
      {/* Sidebar */}
      <aside className={`fixed lg:sticky top-0 left-0 h-screen w-64 bg-charcoal border-r border-white/5 z-50 transform transition-transform lg:transform-none ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="p-6">
          <Link to="/" className="flex items-center gap-3 mb-8">
            <div className="relative">
              <div className="absolute inset-0 bg-gold/20 rounded-full blur-md"></div>
              <img 
                src={RAFHA_LOGO} 
                alt="Rafha" 
                className="w-11 h-11 object-contain relative z-10 drop-shadow-[0_0_8px_rgba(212,175,55,0.3)]" 
              />
            </div>
            <span className="font-heading font-bold text-white text-lg">Rafha</span>
          </Link>
          
          <nav className="space-y-2">
            {menuItems.map((item) => (
              <Link
                key={item.label}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-white/60 hover:bg-white/5 hover:text-white transition-colors"
                data-testid={`sidebar-${item.label.toLowerCase()}`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
        
        {/* User Info */}
        <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-white/5">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
              ) : (
                <User className="w-5 h-5 text-gold" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium truncate">{user?.name}</p>
              <p className="text-white/40 text-xs capitalize">{user?.role}</p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={handleLogout}
            className="w-full border-white/10 text-white/60 hover:bg-red-500/10 hover:text-red-500 hover:border-red-500/30 rounded-full"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>
      
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      {/* Main Content */}
      <main className="flex-1 min-h-screen">
        {/* Mobile header */}
        <div className="lg:hidden sticky top-0 z-30 bg-charcoal border-b border-white/5 px-4 py-3 flex items-center justify-between">
          <button 
            onClick={() => setSidebarOpen(true)}
            className="text-white p-2"
          >
            <Menu className="w-6 h-6" />
          </button>
          <Link to="/">
            <img src={RAFHA_LOGO} alt="Rafha" className="w-8 h-8 object-contain" />
          </Link>
          <div className="w-10"></div>
        </div>
        
        {children}
      </main>
    </div>
  );
};

export default DashboardLayout;
